import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Shell from "../components/Shell.jsx";
import { api } from "../lib/api.js";
import { resizePhoto } from "../lib/image.js";
import { centsToDisplay } from "../lib/money.js";
import { Field, SelectWithOther, SaveButton, inputCls } from "../components/ui.jsx";
import ContactActions from "../components/SocialIcons.jsx";
import { TASK_ICON, TaskForm } from "./Crm.jsx";

const STAGE_LABELS = { inquiry: "Inquiry", proposal: "Proposal", booked: "Booked", delivered: "Delivered", paid: "Paid", lost: "Lost" };
const ACT_ICON = { note: "📝", call: "📞", email: "✉️", meeting: "🤝" };
const SOURCES = ["Referral", "Website", "Bootcamp form", "LinkedIn", "Chamber of Commerce", "Event", "Business card"];

function linkLabel(url) {
  try {
    const h = new URL(url).hostname.replace(/^www\./, "");
    const map = {
      "linkedin.com": "💼 LinkedIn",
      "facebook.com": "📘 Facebook",
      "instagram.com": "📸 Instagram",
      "x.com": "🐦 X",
      "twitter.com": "🐦 X",
      "youtube.com": "▶️ YouTube",
      "tiktok.com": "🎵 TikTok",
    };
    for (const k in map) if (h === k || h.endsWith(`.${k}`)) return map[k];
    return `🌐 ${h}`;
  } catch {
    return "🌐 link";
  }
}

// Tap the avatar → iOS offers "Take Photo / Photo Library" (plain image input, no capture attr).
function Avatar({ contact, version, onUploaded }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const initials = contact.name
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  async function pick(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const blob = await resizePhoto(file, 512, 0.8);
      const fd = new FormData();
      fd.append("photo", blob, "photo.jpg");
      const res = await fetch(`/api/crm/contacts/${contact.id}/photo`, { method: "POST", body: fd, credentials: "include" });
      if (!res.ok) throw new Error("upload failed");
      onUploaded();
    } catch {
      alert("Couldn't save the photo — try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        className="relative h-16 w-16 shrink-0 overflow-hidden rounded-full bg-navy-700/10"
      >
        {contact.photo_key ? (
          <img src={`/api/crm/contacts/${contact.id}/photo?v=${version}`} alt="" className="h-full w-full object-cover" />
        ) : (
          <span className="flex h-full w-full items-center justify-center text-lg font-semibold text-navy-700">{initials}</span>
        )}
        <span className="absolute inset-x-0 bottom-0 bg-black/40 py-0.5 text-center text-[9px] font-semibold text-white">
          {busy ? "…" : "📷"}
        </span>
      </button>
      <input ref={inputRef} type="file" accept="image/*" hidden onChange={pick} />
    </>
  );
}

function EditContact({ contact, onDone, onCancel }) {
  // Older rows may predate the first/last split — derive from the display name.
  const full = (contact.name || "").trim();
  const cut = full.lastIndexOf(" ");
  const [f, setF] = useState({
    first_name: contact.first_name || (cut > 0 ? full.slice(0, cut) : full),
    last_name: contact.last_name || (cut > 0 ? full.slice(cut + 1) : ""),
    title: contact.title || "",
    company: contact.company || "",
    type: contact.type || "lead",
    email: contact.email || "",
    phone: contact.phone || "",
    address: contact.address || "",
    website: contact.website || "",
    source: contact.source || "",
    notes: contact.notes || "",
    links: contact.links || "",
    training_url: contact.training_url || "",
    facebook_url: contact.facebook_url || "",
    linkedin_url: contact.linkedin_url || "",
    twitter_url: contact.twitter_url || "",
    instagram_url: contact.instagram_url || "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  async function submit(e) {
    e.preventDefault();
    if (!f.first_name.trim() && !f.last_name.trim()) return setError("First or last name is required.");
    setBusy(true);
    setError("");
    try {
      await api(`/api/crm/contacts/${contact.id}`, {
        method: "PUT",
        body: { ...f, lifecycle_stage: contact.lifecycle_stage, owner: contact.owner },
      });
      onDone();
    } catch (err) {
      setError(err.message || "Save failed");
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-3 rounded-2xl bg-white p-4 shadow-sm">
      <div className="grid grid-cols-2 gap-3">
        <Field label="First name">
          <input value={f.first_name} onChange={set("first_name")} className={inputCls} />
        </Field>
        <Field label="Last name">
          <input value={f.last_name} onChange={set("last_name")} className={inputCls} />
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Job title">
          <input value={f.title} onChange={set("title")} className={inputCls} placeholder="e.g. Ops Manager" />
        </Field>
        <Field label="Company">
          <input value={f.company} onChange={set("company")} className={inputCls} />
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Type">
          <select value={f.type} onChange={set("type")} className={inputCls}>
            <option value="lead">Lead</option>
            <option value="client">Client</option>
            <option value="vendor">Vendor</option>
            <option value="contractor">Contractor</option>
            <option value="investor">Investor</option>
          </select>
        </Field>
        <Field label="Source">
          <SelectWithOther value={f.source} onChange={(v) => setF((s) => ({ ...s, source: v }))} options={SOURCES} />
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Email">
          <input type="email" value={f.email} onChange={set("email")} className={inputCls} />
        </Field>
        <Field label="Phone">
          <input value={f.phone} onChange={set("phone")} className={inputCls} />
        </Field>
      </div>
      <Field label="Business address">
        <input value={f.address} onChange={set("address")} className={inputCls} placeholder="Street, city, state ZIP" />
      </Field>
      <Field label="Company website">
        <input value={f.website} onChange={set("website")} className={inputCls} placeholder="theirsite.com" inputMode="url" />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Facebook">
          <input value={f.facebook_url} onChange={set("facebook_url")} className={inputCls} placeholder="URL or handle" inputMode="url" />
        </Field>
        <Field label="LinkedIn">
          <input value={f.linkedin_url} onChange={set("linkedin_url")} className={inputCls} placeholder="URL or handle" inputMode="url" />
        </Field>
        <Field label="X / Twitter">
          <input value={f.twitter_url} onChange={set("twitter_url")} className={inputCls} placeholder="URL or @handle" inputMode="url" />
        </Field>
        <Field label="Instagram">
          <input value={f.instagram_url} onChange={set("instagram_url")} className={inputCls} placeholder="URL or @handle" inputMode="url" />
        </Field>
      </div>
      <Field label="Other links — one URL per line (website, portfolio…)">
        <textarea value={f.links} onChange={set("links")} rows={2} className={inputCls} placeholder="https://theirsite.com" />
      </Field>
      <Field label="Training URL (their progress page)">
        <input value={f.training_url} onChange={set("training_url")} className={inputCls} placeholder="https://…" inputMode="url" />
      </Field>
      <Field label="Notes">
        <textarea value={f.notes} onChange={set("notes")} rows={2} className={inputCls} />
      </Field>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="flex gap-2">
        <button type="button" onClick={onCancel} className="flex-1 rounded-xl border border-slate-300 py-3 text-sm font-semibold text-slate-500">
          Cancel
        </button>
        <div className="flex-1">
          <SaveButton busy={busy}>Save</SaveButton>
        </div>
      </div>
    </form>
  );
}

/* Contractor paperwork: W-9 / NDA signing requests. The link is shown once —
   Mike texts/emails it himself (assisted pattern; no email service needed). */
const DOC_LABEL = { w9: "W-9", nda: "NDA" };
const DOC_STATUS_STYLE = {
  sent: "bg-slate-100 text-slate-500",
  viewed: "bg-amber-100 text-amber-700",
  signed: "bg-emerald-100 text-emerald-700",
  void: "bg-slate-100 text-slate-400 line-through",
};

function DocumentsCard({ contactId }) {
  const navigate = useNavigate();
  const [docs, setDocs] = useState([]);
  const [newLink, setNewLink] = useState(null); // { doc_type, link }
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = () => api(`/api/crm/contacts/${contactId}/documents`).then((d) => setDocs(d.documents)).catch(() => {});
  useEffect(() => {
    load();
  }, [contactId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function create(docType) {
    setBusy(true);
    try {
      const r = await api(`/api/crm/contacts/${contactId}/documents`, { method: "POST", body: { doc_type: docType } });
      setNewLink({ doc_type: docType, link: r.link });
      setCopied(false);
      load();
    } finally {
      setBusy(false);
    }
  }

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(newLink.link);
      setCopied(true);
    } catch {
      /* the field below is selectable as fallback */
    }
  }

  return (
    <section>
      <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">✍️ Paperwork (W-9 / NDA)</p>
      <div className="space-y-3 rounded-2xl bg-white p-4 shadow-sm">
        <div className="flex gap-2">
          <button onClick={() => create("w9")} disabled={busy} className="flex-1 rounded-xl border border-navy-600/30 bg-navy-700/5 py-2.5 text-sm font-semibold text-navy-700 disabled:opacity-50">
            + W-9 request
          </button>
          <button onClick={() => create("nda")} disabled={busy} className="flex-1 rounded-xl border border-navy-600/30 bg-navy-700/5 py-2.5 text-sm font-semibold text-navy-700 disabled:opacity-50">
            + NDA request
          </button>
        </div>

        {newLink && (
          <div className="rounded-xl bg-amber-50 p-3">
            <p className="text-xs font-semibold text-amber-900">
              {DOC_LABEL[newLink.doc_type]} signing link — shown once, so send it now (text or email):
            </p>
            <p className="mt-1 break-all rounded-lg bg-white px-2 py-1.5 text-[11px] text-slate-600">{newLink.link}</p>
            <div className="mt-2 flex gap-3">
              <button onClick={copyLink} className="text-xs font-semibold text-navy-700">
                {copied ? "Copied ✓" : "📋 Copy link"}
              </button>
              {navigator.share && (
                <button onClick={() => navigator.share({ text: `Please sign this for AI Engines: ${newLink.link}` }).catch(() => {})} className="text-xs font-semibold text-navy-700">
                  📤 Share
                </button>
              )}
              <button onClick={() => setNewLink(null)} className="text-xs text-slate-400">
                Done
              </button>
            </div>
          </div>
        )}

        {docs.length > 0 && (
          <div className="divide-y divide-slate-100">
            {docs.map((d) => (
              <div key={d.id} className="flex items-center gap-2 py-2.5">
                <button
                  onClick={() => (d.status === "signed" ? navigate(`/documents/${d.id}`) : null)}
                  className="min-w-0 flex-1 text-left"
                >
                  <p className="text-sm font-medium text-slate-800">
                    {DOC_LABEL[d.doc_type] || d.doc_type}
                    {d.tin_last4 ? <span className="ml-2 text-xs text-slate-400">TIN ···{d.tin_last4}</span> : null}
                  </p>
                  <p className="text-xs text-slate-400">
                    {d.status === "signed" ? `signed ${(d.signed_at || "").slice(0, 10)} — tap to view` : `sent ${(d.sent_at || "").slice(0, 10)}${d.viewed_at ? " · opened" : ""}`}
                  </p>
                </button>
                <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${DOC_STATUS_STYLE[d.status] || ""}`}>{d.status}</span>
                {d.status !== "signed" && d.status !== "void" && (
                  <button
                    onClick={async () => {
                      if (!window.confirm("Withdraw this signing link?")) return;
                      await api(`/api/crm/contacts/${contactId}/documents`, { method: "POST", body: { action: "void", document_id: d.id } });
                      load();
                    }}
                    className="shrink-0 text-xs text-slate-300"
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export default function CrmContact() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [showDone, setShowDone] = useState(false);
  const [photoVersion, setPhotoVersion] = useState(0);
  const [note, setNote] = useState("");
  const [noteType, setNoteType] = useState("note");

  const load = () => api(`/api/crm/contacts/${id}`).then(setData).catch(() => setError("Contact not found."));
  useEffect(() => {
    load();
  }, [id]);

  async function addNote(e) {
    e.preventDefault();
    if (!note.trim()) return;
    await api("/api/crm/activities", { method: "POST", body: { contact_id: id, type: noteType, body: note } });
    setNote("");
    load();
  }
  async function remove() {
    if (!window.confirm("Delete this contact? (Kept in the audit log.)")) return;
    await api(`/api/crm/contacts/${id}`, { method: "DELETE" });
    navigate("/crm");
  }

  if (error) return <Shell title="Contact" back><p className="text-sm text-red-600">{error}</p></Shell>;
  if (!data) return <Shell title="Contact" back><p className="text-center text-sm text-slate-400">Loading…</p></Shell>;

  const { contact, deals, activities, tasks } = data;
  const links = (contact.links || "").split("\n").map((s) => s.trim()).filter(Boolean);

  return (
    <Shell title={contact.name} back>
      {editing ? (
        <EditContact contact={contact} onDone={() => { setEditing(false); load(); }} onCancel={() => setEditing(false)} />
      ) : (
        <section className="rounded-2xl bg-white p-5 shadow-sm">
          <div className="flex items-start gap-4">
            <Avatar contact={contact} version={photoVersion} onUploaded={() => { setPhotoVersion((v) => v + 1); load(); }} />
            <div className="min-w-0 flex-1">
              <p className="text-lg font-semibold text-slate-800">{contact.name}</p>
              {contact.title && <p className="text-sm text-slate-500">{contact.title}</p>}
              {contact.company && (
                <button
                  onClick={() => navigate(`/crm?tab=contacts&q=${encodeURIComponent(contact.company)}`)}
                  className="text-sm font-medium text-navy-700"
                >
                  🏢 {contact.company} ›
                </button>
              )}
            </div>
            <div className="flex shrink-0 flex-col items-end gap-2">
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-slate-500">{contact.type}</span>
              <button onClick={() => setEditing(true)} className="text-sm font-semibold text-navy-700">
                ✏️ Edit
              </button>
            </div>
          </div>
          {/* One-tap actions: call, email (opens the device's default mail app),
              and whichever social profiles this record has. */}
          <div className="mt-4">
            <ContactActions person={contact} />
          </div>
          <div className="mt-3 space-y-0.5 text-xs text-slate-400">
            {contact.email && <p className="truncate">{contact.email}</p>}
            {contact.phone && <p>{contact.phone}</p>}
            {contact.website && (
              <a href={contact.website} target="_blank" rel="noreferrer" className="block truncate text-navy-700">
                🌐 {contact.website.replace(/^https?:\/\//, "")}
              </a>
            )}
            {contact.address && (
              <a
                href={`https://maps.apple.com/?daddr=${encodeURIComponent(contact.address)}`}
                target="_blank"
                rel="noreferrer"
                className="block text-navy-700"
              >
                📍 {contact.address}
              </a>
            )}
            {contact.source && <p>Source: {contact.source}</p>}
            {contact.notes && <p className="text-slate-500">{contact.notes}</p>}
          </div>
          {links.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {links.map((u) => (
                <a key={u} href={u} target="_blank" rel="noreferrer" className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-semibold text-navy-700">
                  {linkLabel(u)}
                </a>
              ))}
            </div>
          )}
          {contact.training_url && (
            <a
              href={contact.training_url}
              target="_blank"
              rel="noreferrer"
              className="mt-3 block rounded-xl bg-navy-700 py-2.5 text-center text-sm font-semibold text-white"
            >
              🎓 Open training progress ↗
            </a>
          )}
        </section>
      )}

      {/* Schedule a follow-up right from the record */}
      {scheduling ? (
        <TaskForm contact={{ id: contact.id, name: contact.name }} lockContact onSaved={() => { setScheduling(false); load(); }} />
      ) : (
        <button
          onClick={() => setScheduling(true)}
          className="w-full rounded-xl border-2 border-dashed border-navy-600/40 bg-navy-700/5 py-3 text-sm font-semibold text-navy-700"
        >
          ＋ Schedule follow-up / meeting
        </button>
      )}

      {deals.length > 0 && (
        <section>
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">Engagements</p>
          <div className="divide-y divide-slate-100 rounded-2xl bg-white shadow-sm">
            {deals.map((d) => (
              <div key={d.id} className="flex items-center justify-between px-4 py-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-slate-800">{d.title}</p>
                  <p className="text-xs text-slate-400">{STAGE_LABELS[d.stage]}{d.offering_tag ? ` · ${d.offering_tag}` : ""}</p>
                </div>
                <p className="text-sm font-semibold text-slate-700">{centsToDisplay(d.value_cents)}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {tasks.filter((t) => !t.done).length > 0 && (
        <section>
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">Open follow-ups</p>
          <div className="divide-y divide-slate-100 rounded-2xl bg-white shadow-sm">
            {tasks.filter((t) => !t.done).map((t) => (
              <div key={t.id} className="flex items-center gap-3 px-4 py-3">
                <button
                  className="text-xl"
                  onClick={async () => {
                    await api(`/api/crm/tasks/${t.id}`, { method: "PUT", body: { done: true } });
                    load();
                  }}
                >
                  ⬜️
                </button>
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-slate-800">
                    {TASK_ICON[t.task_type] || "🔔"} {t.title}
                  </p>
                  {t.due_date && <p className="text-xs text-slate-400">due {t.due_date}</p>}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Per-contact completed history (master list lives on the Tasks tab) */}
      {tasks.some((t) => t.done) && (
        <section>
          <button
            onClick={() => setShowDone((s) => !s)}
            className="mb-1 flex w-full items-center justify-between text-xs font-semibold uppercase tracking-wide text-slate-400"
          >
            <span>✅ Completed ({tasks.filter((t) => t.done).length})</span>
            <span>{showDone ? "▲" : "▼"}</span>
          </button>
          {showDone && (
            <div className="divide-y divide-slate-100 rounded-2xl bg-white shadow-sm">
              {tasks
                .filter((t) => t.done)
                .sort((a, b) => (b.completed_at || "").localeCompare(a.completed_at || ""))
                .map((t) => (
                  <div key={t.id} className="flex items-center gap-3 px-4 py-3">
                    <button
                      className="text-xl"
                      title="Reopen"
                      onClick={async () => {
                        await api(`/api/crm/tasks/${t.id}`, { method: "PUT", body: { done: false } });
                        load();
                      }}
                    >
                      ✅
                    </button>
                    <div className="min-w-0">
                      <p className="truncate text-sm text-slate-500">
                        {TASK_ICON[t.task_type] || "🔔"} {t.title}
                      </p>
                      <p className="text-xs text-slate-400">done {t.completed_at ? t.completed_at.slice(0, 10) : ""}</p>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </section>
      )}

      <DocumentsCard contactId={id} />

      {/* Activity log + quick add */}
      <section>
        <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">Activity</p>
        <form onSubmit={addNote} className="space-y-2 rounded-2xl bg-white p-4 shadow-sm">
          <div className="flex gap-1">
            {["note", "call", "email", "meeting"].map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setNoteType(t)}
                className={`flex-1 rounded-lg py-1.5 text-lg ${noteType === t ? "bg-navy-700/10" : "bg-slate-50"}`}
              >
                {ACT_ICON[t]}
              </button>
            ))}
          </div>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Log a note, call, email, or meeting…" className={inputCls} />
          <button className="w-full rounded-xl bg-navy-700 py-2.5 text-sm font-semibold text-white">Log activity</button>
        </form>
        <div className="mt-2 space-y-2">
          {activities.map((a) => (
            <div key={a.id} className="rounded-xl bg-white p-3 text-sm shadow-sm">
              <p className="text-slate-700">{ACT_ICON[a.type]} {a.body}</p>
              <p className="mt-1 text-xs text-slate-400">{a.at}</p>
            </div>
          ))}
        </div>
      </section>

      <button onClick={remove} className="w-full rounded-xl border border-red-200 py-3 text-sm font-semibold text-red-600">
        Delete contact
      </button>
    </Shell>
  );
}
